// ABOUTME: The talent's own applications as pro-core returns them from GET /talent/applications, typed by hand from
// ABOUTME: TalentApplicationHttp, TalentApplicationStepHttp and TalentApplicationEventHttp on the ats/m1 contract.
import type { AvailabilityDay } from 'modules/availability/types';
import type { InterviewKind, InterviewStatus } from 'modules/interviews/types';

/** ACTIVE while the application has no outcome, else the outcome itself (TalentApplicationStatus). */
export enum TalentApplicationStatus {
  ACTIVE = 'ACTIVE',
  HIRED = 'HIRED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN'
}

/**
 * The state of one tracker step, decided by the backend from the transition history (TalentApplicationStepState).
 * DONE means "the application was in this step before the current one", not "positioned before the current one", so
 * a move back from `proposed` to `first_call` arrives as `proposed` DONE and `first_call` CURRENT. The full tracker
 * draws a tick only before the current step (trackerSteps, #484): a tick after it read as a step already passed.
 */
export enum TalentApplicationStepState {
  DONE = 'DONE',
  CURRENT = 'CURRENT',
  UPCOMING = 'UPCOMING'
}

/** How a candidacy ends when an exit step is entered. The same names as the outcome statuses. */
export type TalentApplicationOutcome = 'HIRED' | 'REJECTED' | 'WITHDRAWN';

/** One step of the tracker; label and hint arrive in the request language (Accept-Language). */
export interface TalentApplicationStep {
  key: string;
  label: string;
  hint: string | null;
  /** The outcome this step stands for when it is an exit; null (or absent) for a step on the way. */
  outcome?: TalentApplicationOutcome | null;
  state: TalentApplicationStepState;
}

/** One entry of the detail's visible timeline, oldest first. Detail only, so never present on the list. */
export interface TalentApplicationEvent {
  stepKey: string;
  stepLabel: string;
  occurredAt: string;
  reason: string | null;
}

export interface TalentApplication {
  refId: string;
  jobOpeningRefId: string;
  jobOpeningTitle: string;
  companyName: string;
  desiredStartDate: string | null;
  status: TalentApplicationStatus;
  /** The talent label of the current step, null when that step is not shown to talents. */
  currentStepLabel: string | null;
  /** The talent-visible steps of the opening's process in position order. */
  steps: TalentApplicationStep[];
  /** The reason of the latest move when it was shared with the talent. */
  sharedReason: string | null;
  /** When the application entered the process; null while it has no transition yet. */
  appliedAt: string | null;
  /** When the latest move happened; null while the application has no transition yet. */
  lastChangedAt: string | null;
  /** Detail only: null on the list. */
  canWithdraw: boolean | null;
  /** Detail only: null on the list. */
  events: TalentApplicationEvent[] | null;
  /**
   * Detail only, while PRO_INTERVIEWS is on: the latest interview round. Absent (not null) when no step has asked for
   * an interview; the card itself is GET /talent/interviews/{refId}.
   */
  interview?: TalentApplicationInterview;
  /**
   * PJL (zenjob/pro-core-service#2011, served by #2106): everything below is sent only while PRO_APPLY_V2 and
   * PRO_JOB_LISTING are on for the talent; off, the keys are absent and the application is today's.
   */
  jobListingRefId?: string;
  companyLogoImageRefId?: string;
  /** Hidden by the talent from their own list; never seen by Zenjob or the company. */
  hidden?: boolean;
  /** What the application waits for from the talent; it brings a hidden row back. */
  needsAction?: TalentApplicationNeedsAction;
  /** Moved since the talent last opened it. */
  hasNews?: boolean;
  /** Detail only: what the talent applied with. */
  appliedWith?: TalentApplicationAppliedWith;
  /** Detail only: a message from Zenjob about this application, e.g. a raised rate. */
  notice?: TalentApplicationNotice;
  /** Detail only: how the first call will happen, while that step is current. */
  firstCall?: TalentApplicationFirstCall;
  /** Once hired: the job itself. */
  employment?: TalentEmployment;
}

/** PJL: which rows GET /talent/applications returns; without a filter, today's list. */
export interface ApplicationsFilter {
  includeHidden?: boolean;
  needsAction?: boolean;
}

/** PJL: the week sent with an application, changed after applying. */
export interface AppliedWeekUpdate {
  days: AvailabilityDay[];
  weeklyMinutes?: number;
  startFrom?: string;
  saveToProfile?: boolean;
}

export type NeedsActionType = 'CV' | 'INTERVIEW' | 'CONTRACT';

export interface TalentApplicationNeedsAction {
  type: NeedsActionType;
  dueAt?: string | null;
}

/** Read from the application's snapshot (apply v2); each part absent when neither the snapshot nor the listing has it. */
export interface TalentApplicationAppliedWith {
  hourlyRate?: number;
  weeklyMinutes?: number;
  /** The days the talent sent, already summarised by pro-core, e.g. "Mo–Mi ab 12, Fr". */
  daysSummary?: string;
  startFrom?: string;
  versionNo?: number;
}

export interface TalentApplicationNotice {
  type: 'EARNINGS_RAISED';
  hourlyRate: number;
}

export interface TalentApplicationFirstCall {
  /** AI: the call may come from Zenjob's AI assistant; PERSON: a person calls. */
  mode: 'AI' | 'PERSON';
  /** The talent's free times the call aims for, summarised, e.g. "Mo–Mi ab 12 Uhr, Fr". */
  window?: string;
}

export type ContractStatus = 'PREPARING' | 'SENT' | 'SIGNED';

export interface TalentEmployment {
  assignmentRefId: string;
  /** The assignment's first day, Berlin, "yyyy-MM-dd". */
  startDate: string;
  /** What to do before the first day, each ticked when done. pro-core sends [] until a checklist exists. */
  beforeStart: { type: string; label: string; done: boolean }[];
  /** Absent without a contract. pro-core sends no signUrl: the link goes out in the e-sign provider's mail. */
  contract?: { status: ContractStatus; signUrl?: string | null };
}

/** TalentApplicationInterviewHttp: which interview, where it stands, and the held or booked start (null while none). */
export interface TalentApplicationInterview {
  refId: string;
  status: InterviewStatus;
  kind: InterviewKind;
  startsAt: string | null;
}

/** The DataHttp envelope every pro-core response wears. */
export interface ApplicationsResponse {
  data: TalentApplication[];
}

/** The same envelope around one application, as the detail and the withdrawal answer it. */
export interface ApplicationResponse {
  data: TalentApplication;
}
